import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Upload, Image as ImageIcon, Search, MessageCircle, Package, Inbox, CheckCircle2, DollarSign, BarChart3 } from "lucide-react";
import PhotoDashboardModal from "../components/PhotoDashboardModal";
import StoriesRing from "../components/StoriesRing";
import MerchantPhotoThumb from "../components/MerchantPhotoThumb";
import { money, num, meters } from "../components/accounting/format";
import { valueSize } from "../components/accounting/ui";

const ActionCard = ({ to, icon: Icon, title, subtitle, testid }) => (
  <Link to={to} data-testid={testid}
    className="group bg-white border border-[#7AB2B2] rounded-2xl p-6 hover:border-[#09637E] hover:shadow-lg transition-all">
    <div className="w-14 h-14 rounded-xl bg-[#EBF4F6] group-hover:bg-[#09637E] text-[#09637E] group-hover:text-white flex items-center justify-center mb-4 transition-colors">
      <Icon size={28} />
    </div>
    <h3 className="text-xl font-extrabold text-[#333333] mb-1">{title}</h3>
    <p className="text-sm text-[#666666]">{subtitle}</p>
  </Link>
);

const StatCard = ({ icon: Icon, label, value, tone = "text-[#09637E]", ltr = false, testid }) => (
  <div className="bg-white border border-[#7AB2B2] rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-3 md:contents min-w-0">
      <div className={`w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-lg bg-[#EBF4F6] flex items-center justify-center ${tone}`}>
        <Icon size={24} />
      </div>
      <div className="text-xs md:text-sm text-[#666666] font-bold leading-tight min-w-0 md:hidden">{label}</div>
    </div>
    <div className="min-w-0 flex-1">
      <div className="hidden md:block text-sm text-[#666666] font-bold">{label}</div>
      <div className={`${valueSize(value)} font-extrabold tabular-nums break-words min-w-0 ${tone}`}><bdi dir={ltr ? "ltr" : "auto"}>{value}</bdi></div>
    </div>
  </div>
);

const MerchantDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ photo_count: 0, incoming_reservations: 0, completed_sales: 0, meters_sold: 0, revenue_usd: 0 });
  const [photos, setPhotos] = useState([]);
  const [dashboardPhotoId, setDashboardPhotoId] = useState(null);

  useEffect(() => {
    api.get("/stats/merchant").then((r) => setStats(r.data)).catch((e) => toast.error(arabicError(e)));
    api.get("/photos/mine").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  // Rotating motivational message
  const motivations = [
    "منصة إمداد: حيث يلتقي التاجر بما ينقصه",
    "منصة إمداد: منصة التجار الأولى في سوريا",
    "أكمل مخزونك. ابدأ مشروعك. مع منصة إمداد",
  ];
  const motivation = motivations[new Date().getDate() % motivations.length];

  return (
    <div className="space-y-8" data-testid="merchant-dashboard">
      <StoriesRing />
      {/* Motivational banner */}
      <div className="relative overflow-hidden bg-gradient-to-l from-[#09637E] to-[#088395] text-white rounded-2xl p-6 md:p-8" data-testid="motivational-banner">
        <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <p className="text-lg md:text-2xl font-extrabold leading-relaxed">"{motivation}"</p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">
            مرحباً، <span className="text-[#09637E]">{fullName || user?.store_name}</span>
          </h1>
          <p className="text-[#666666] mt-1">ماذا تريد أن تفعل اليوم؟</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="صور منتجاتي" value={num(stats.photo_count)} testid="stat-my-photos" />
        <StatCard icon={Inbox} label="حجوزات واردة" value={num(stats.incoming_reservations)} testid="stat-incoming-reservations" />
        <StatCard icon={CheckCircle2} label="طلبات تم بيعها" value={num(stats.completed_sales)} tone="text-green-700" testid="stat-completed-sales" />
        <StatCard icon={DollarSign} label="إجمالي المبيعات" value={money(stats.revenue_usd)} tone="text-[#088395]" ltr testid="stat-revenue" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ActionCard to="/upload" icon={Upload} title="رفع صورة جديدة" subtitle="أضف منتجاً لمخزونك" testid="action-upload" />
        <ActionCard to="/my-photos" icon={ImageIcon} title="صور منتجاتي" subtitle="إدارة منتجاتك" testid="action-my-photos" />
        <ActionCard to="/search" icon={Search} title="البحث عن منتج" subtitle="ابحث بالصورة عن بلاط مشابه" testid="action-search" />
        <ActionCard to="/messages" icon={MessageCircle} title="الرسائل" subtitle="الحجوزات والمحادثات" testid="action-messages" />
        <ActionCard to="/insights" icon={BarChart3} title="إحصائياتي" subtitle="تقريرك الشهري ومشاهدات منتجاتك" testid="action-insights" />
      </div>

      {photos.length > 0 && (
        <section data-testid="inventory-table">
          <h2 className="text-2xl font-extrabold text-[#333333] mb-4">مخزوني — نظرة عامة</h2>
          <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm">
                <thead className="bg-[#EBF4F6]">
                  <tr>
                    <th className="p-3 font-bold">الصورة</th>
                    <th className="p-3 font-bold">الصنف</th>
                    <th className="p-3 font-bold">اللون</th>
                    <th className="p-3 font-bold">بلد المنشأ</th>
                    <th className="p-3 font-bold">المقاس</th>
                    <th className="p-3 font-bold">السعر</th>
                    <th className="p-3 font-bold">المتوفر</th>
                    <th className="p-3 font-bold">المباع</th>
                    <th className="p-3 font-bold">الإجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {photos.map((p) => {
                    const avail = p.quantity_available ?? p.quantity ?? 0;
                    const sold = p.quantity_sold ?? 0;
                    return (
                      <tr key={p.id} className="border-t border-[#EBF4F6] hover:bg-[#EBF4F6]/40" data-testid={`inv-row-${p.id}`}>
                        <td className="p-2">
                          <div className="w-14 h-14 rounded-lg overflow-hidden">
                            <MerchantPhotoThumb photoId={p.id} testid={`inv-thumb-${p.id}`} />
                          </div>
                        </td>
                        <td className="p-3 font-bold">{p.category} · {p.type}</td>
                        <td className="p-3 text-[#666666]">{p.color || "—"}</td>
                        <td className="p-3 text-[#666666]">{p.country}</td>
                        <td className="p-3">{p.size}</td>
                        <td className="p-3 text-[#09637E] font-bold whitespace-nowrap tabular-nums"><bdi dir="ltr">{money(p.price_usd ?? p.price ?? 0)}</bdi></td>
                        <td className="p-3 font-bold whitespace-nowrap tabular-nums">{meters(avail)}</td>
                        <td className="p-3 text-green-700 font-bold whitespace-nowrap tabular-nums">{meters(sold)}</td>
                        <td className="p-3">
                          <button onClick={() => setDashboardPhotoId(p.id)} data-testid={`inv-dashboard-${p.id}`}
                            className="h-9 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-xs font-bold flex items-center gap-1">
                            <BarChart3 size={14} /> لوحة
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {dashboardPhotoId && (
        <PhotoDashboardModal photoId={dashboardPhotoId} onClose={() => setDashboardPhotoId(null)} />
      )}
    </div>
  );
};

export default MerchantDashboard;
